// 공유 카드 → 영상(mp4) 렌더 클라이언트. 렌더 서버(bwek-card)에 카드 데이터를 POST하고 mp4를 받는다.
// 실패/타임아웃이면 throw → 호출부가 이미지 공유로 폴백(루프 안 끊김).

import { Capacitor } from "@capacitor/core";
import type { CardData } from "../screens/ResultCard";

const RAW = (typeof import.meta !== "undefined" && import.meta.env?.VITE_RENDER_ENDPOINT) || "";

// 네이티브(capacitor://localhost)는 절대 URL, 웹은 같은 오리진 상대경로(/api/card) — judgeClient와 동일.
const ENDPOINT = RAW ? (Capacitor.isNativePlatform() ? RAW : RAW.replace(/^https?:\/\/[^/]+/, "")) : "";

export const videoRenderEnabled = Boolean(ENDPOINT);

// Blob → base64(데이터 부분만). FileReader 사용 — 큰 버퍼를 spread하다 RangeError 나는 문제 회피.
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = typeof r.result === "string" ? r.result : "";
      resolve(s.includes(",") ? s.slice(s.indexOf(",") + 1) : s);
    };
    r.onerror = () => reject(r.error ?? new Error("base64 변환 실패"));
    r.readAsDataURL(blob);
  });
}

export interface RenderedVideo {
  base64: string; // 네이티브 저장/공유(Filesystem write)용
  blob: Blob; // 웹 다운로드/Web Share용
}

/** 카드 데이터 → mp4 렌더. 콜드스타트 대비 타임아웃 넉넉히(기본 35s, 로딩 연출이 덮음). */
export async function renderCardVideo(data: CardData, timeoutMs = 35000): Promise<RenderedVideo> {
  if (!ENDPOINT) throw new Error("render endpoint not set");
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(data),
      signal: ctrl.signal,
    });
    if (!res.ok) throw new Error(`render: HTTP ${res.status}`);
    const blob = await res.blob();
    return { base64: await blobToBase64(blob), blob };
  } finally {
    clearTimeout(timer);
  }
}
